import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSavedCards,
  saveCard,
  updateCard,
  deleteCard,
  getDashboards,
  createDashboard,
} from '../api/analyticsApi';
import type { SavedCard, Dashboard } from '../types/analytics.types';

export function useSavedCards() {
  return useQuery({
    queryKey: ['savedCards'],
    queryFn: getSavedCards,
    staleTime: 30000,
  });
}

export function useDashboards() {
  return useQuery({
    queryKey: ['dashboards'],
    queryFn: getDashboards,
    staleTime: 30000,
  });
}

export function useSaveCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (card: Omit<SavedCard, 'id' | 'createdAt' | 'updatedAt'>) =>
      saveCard(card),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedCards'] });
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
  });
}

export function useUpdateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SavedCard> }) =>
      updateCard(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedCards'] });
    },
  });
}

export function useDeleteCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedCards'] });
    },
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => createDashboard(name),
    onSuccess: (newDashboard: Dashboard) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      return newDashboard;
    },
  });
}
